"use client";
import React from "react";
import { motion } from "framer-motion";
import {
  BsShieldCheck,
  BsLightbulb,
  BsPeople,
  BsBook,
  BsCashCoin,
  BsHeartPulse,
  BsClockHistory,
  BsMortarboard,
  BsAward,
  BsRocketTakeoff,
  BsEnvelopeFill,
  BsTelephoneFill,
} from "react-icons/bs";
import HeaderInner from "../../utility/HeaderInner";

const Career = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const values = [
    {
      title: "Integrity in Action",
      desc: "We stay true to our commitments.",
      icon: <BsShieldCheck />,
    },
    {
      title: "Innovation with Purpose",
      desc: "Technology for meaningful impact.",
      icon: <BsLightbulb />,
    },
    {
      title: "Collaboration & Trust",
      desc: "Stronger together.",
      icon: <BsPeople />,
    },
    {
      title: "Continuous Learning",
      desc: "For our users and ourselves.",
      icon: <BsBook />,
    },
  ];

  const perks = [
    {
      text: "Competitive compensation & performance incentives",
      icon: <BsCashCoin />,
    },
    { text: "Health & wellness programs", icon: <BsHeartPulse /> },
    { text: "Flexible working options", icon: <BsClockHistory /> },
    { text: "Learning & certification support", icon: <BsMortarboard /> },
    { text: "Recognition & rewards for top performance", icon: <BsAward /> },
    {
      text: "Inclusive culture with regular team activities",
      icon: <BsPeople />,
    },
  ];

  const jobs = [
    [1, ".Net Developer", "Hyderabad", "3+ Years", "Any Graduate"],
    [2, ".Net Developer", "Chennai", "8+ Years", "Any Graduate"],
    [3, "Project Lead", "Hyderabad", "7+ Years", "Any Graduate"],
    [4, "Project Lead", "Jalgaon", "7+ Years", "Any Graduate"],
    [5, "Operations Executives", "Hyderabad", "2+ Years", "Any Graduate"],
    [
      6,
      "Scanning Operators",
      "Across Karnataka",
      "0-1 Year",
      "+2, ITI, Diploma",
    ],
    [
      7,
      "Scanning Operators",
      "Across Maharashtra",
      "0-1 Year",
      "+2, ITI, Diploma",
    ],
  ];

  return (
    <motion.div initial="hidden" animate="visible" className="w-100 mt-70">
      <HeaderInner title={"Careers"} />

      {/* Header Section */}
      <motion.div variants={fadeInUp} className="container my-5 text-center">
        <h1 className="display-4 fw-bold mb-3">Career at Coempt Edu Teck</h1>
        <p className="lead text-secondary mb-3">
          Transforming Learning. Empowering Futures.
        </p>
        <p className="text-secondary mx-auto" style={{ maxWidth: "700px" }}>
          At Coempt Edu Teck, we believe education has the power to change the
          world. We’re on a mission to create cutting-edge solutions that make
          learning more engaging, accessible, and impactful. If you’re
          passionate about innovation, technology, and education, you’ll find
          your place with us.
        </p>
      </motion.div>

      {/* Life at Coempt */}
      <motion.div variants={fadeInUp} className="container mb-5">
        <div className="row align-items-center g-4">
          <div className="col-md-6">
            <h3 className="h3 fw-bold mb-3">Life at Coempt</h3>
            <p className="text-secondary">
              At Coempt, we’re not just building technology—we’re building a
              community. Our team blends technologists, designers, and
              innovators working together to solve real-world challenges. From
              brainstorming sessions to team celebrations, life here is about
              balance, belonging, and growth.
            </p>
          </div>
          <div className="col-md-6 d-flex justify-content-center">
            <img
              src="https://imgs.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=800&q=80"
              alt="Team"
              className="img-fluid rounded shadow"
              style={{ maxHeight: "320px", objectFit: "cover" }}
            />
          </div>
        </div>
      </motion.div>

      {/* Values Section */}
      <motion.div variants={fadeInUp} className="container mb-5">
        <h3 className="h3 text-center fw-bold mb-4">✨ Our Values</h3>
        <div className="row g-4">
          {values.map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.05 }}
              className="col-12 col-md-6 col-lg-3 text-center p-4 bg-white rounded shadow-sm"
            >
              <div className="text-primary mb-3 fs-1">{item.icon}</div>
              <h5 className="fw-bold mb-2">{item.title}</h5>
              <p className="text-secondary">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Current Openings */}
      <motion.div variants={fadeInUp} className="container mb-5">
        <h3 className="h3 text-center fw-bold mb-4">Current Openings</h3>
        <div className="table-responsive shadow rounded">
          <table className="table table-bordered table-hover mb-0">
            <thead className="table-primary">
              <tr>
                <th>S.No</th>
                <th>Job Title</th>
                <th>Location</th>
                <th>Experience</th>
                <th>Qualification</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job, i) => (
                <tr key={i}>
                  {job.map((data, idx) => (
                    <td key={idx}>{data}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Employee Benefits */}
      <motion.div variants={fadeInUp} className="container mb-5">
        <h3 className="h3 text-center fw-bold mb-4">
          Employee Benefits & Perks
        </h3>
        <div className="row g-3">
          {perks.map((perk, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.02 }}
              className="col-12 col-md-6 d-flex align-items-center p-3 bg-white rounded shadow-sm"
            >
              <div className="text-primary fs-3 me-3">{perk.icon}</div>
              <span className="text-secondary">{perk.text}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Call to Action */}
      <motion.div variants={fadeInUp} className="container mb-5 text-center">
        <div className="p-5 bg-light border rounded-4 shadow-sm">
          <div className="d-flex justify-content-center mb-4">
            <div
              className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "80px", height: "80px", fontSize: "2rem" }}
            >
              <BsRocketTakeoff />
            </div>
          </div>
          <h3 className="fw-bold mb-3">Be Part of Our Journey</h3>
          <p className="text-secondary mb-3 fs-6">
            At <span className="fw-bold text-dark">Coempt</span>, every role
            contributes to building innovative solutions. If you want a career
            where you can{" "}
            <span className="text-primary fw-semibold">
              create, grow, and make an impact
            </span>{" "}
            — join us!
          </p>
          <h5 className="text-success fw-bold mb-2">🚀 Join us Today!</h5>
          <p className="text-secondary fw-semibold">
            <BsEnvelopeFill className="text-danger me-2" />
            <a
              href="mailto:recruit@coempt.in"
              className="text-decoration-underline"
            >
              recruit@coempt.in
            </a>
            <br />
            <BsTelephoneFill className="text-success me-2" />
            <a href="tel:8790886143" className="text-decoration-underline">
              8790886143
            </a>{" "}
            /{" "}
            <a href="tel:8977712977" className="text-decoration-underline">
              8977712977
            </a>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Career;
